import { useEffect, useState } from 'react'
import { collection, query, getDocs, orderBy, doc, deleteDoc, updateDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { formatDate, truncateText } from '../lib/utils'
import { Search, Trash2, Eye, CheckCircle, XCircle, Filter } from 'lucide-react'

interface Letter {
  id: string
  authorUid: string
  body: string
  status: 'approved' | 'rejected'
  rejectionReason?: string
  createdAt: Date
}

export default function LettersPage() {
  const [letters, setLetters] = useState<Letter[]>([])
  const [filteredLetters, setFilteredLetters] = useState<Letter[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'rejected'>('all')
  const [selectedLetter, setSelectedLetter] = useState<Letter | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  useEffect(() => {
    fetchLetters()
  }, [])

  useEffect(() => {
    let filtered = letters

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(l => l.status === statusFilter)
    }

    // Apply search
    if (search) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter(l => 
        l.body.toLowerCase().includes(searchLower) ||
        l.authorUid.toLowerCase().includes(searchLower)
      )
    }

    setFilteredLetters(filtered)
  }, [letters, search, statusFilter])

  const fetchLetters = async () => {
    try {
      const q = query(
        collection(db, 'letters'),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as Letter[]
      setLetters(data)
      setFilteredLetters(data)
    } catch (error) {
      console.error('Error fetching letters:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (letterId: string) => {
    if (!confirm('Bu mektubu silmek istediğinize emin misiniz?')) return
    
    setDeleting(letterId)
    try {
      await deleteDoc(doc(db, 'letters', letterId))
      setLetters(prev => prev.filter(l => l.id !== letterId))
      if (selectedLetter?.id === letterId) {
        setSelectedLetter(null)
      }
    } catch (error) {
      console.error('Error deleting letter:', error)
      alert('Mektup silinemedi')
    } finally {
      setDeleting(null)
    }
  }

  const handleStatusChange = async (letterId: string, newStatus: 'approved' | 'rejected') => {
    try {
      await updateDoc(doc(db, 'letters', letterId), { status: newStatus })
      setLetters(prev => prev.map(l => 
        l.id === letterId ? { ...l, status: newStatus } : l
      ))
      if (selectedLetter?.id === letterId) {
        setSelectedLetter({ ...selectedLetter, status: newStatus })
      }
    } catch (error) {
      console.error('Error updating letter:', error)
      alert('Durum güncellenemedi')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Mektuplar</h2>
        <span className="text-sm text-gray-500">{filteredLetters.length} mektup</span>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Mektup içeriği veya kullanıcı ID'si ara..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'approved' | 'rejected')}
            className="border border-gray-300 rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          >
            <option value="all">Tümü</option>
            <option value="approved">Onaylı</option>
            <option value="rejected">Reddedildi</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Letters List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">İçerik</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Durum</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tarih</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLetters.map((letter) => (
                  <tr 
                    key={letter.id} 
                    className={`hover:bg-gray-50 cursor-pointer ${selectedLetter?.id === letter.id ? 'bg-primary/5' : ''}`}
                    onClick={() => setSelectedLetter(letter)}
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 truncate max-w-xs">
                        {truncateText(letter.body, 50)}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        letter.status === 'approved' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {letter.status === 'approved' ? 'Onaylı' : 'Reddedildi'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(letter.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedLetter(letter)
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(letter.id)
                          }}
                          disabled={deleting === letter.id}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredLetters.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      Mektup bulunamadı
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Letter Detail */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {selectedLetter ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Mektup Detayı</h3>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  selectedLetter.status === 'approved' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {selectedLetter.status === 'approved' ? 'Onaylı' : 'Reddedildi'}
                </span>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedLetter.body}
                </p>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Kullanıcı ID:</span>
                  <span className="text-gray-900 font-mono text-xs">{selectedLetter.authorUid}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Tarih:</span>
                  <span className="text-gray-900">{formatDate(selectedLetter.createdAt)}</span>
                </div>
                {selectedLetter.rejectionReason && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ret Sebebi:</span>
                    <span className="text-red-600">{selectedLetter.rejectionReason}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-200">
                {selectedLetter.status === 'rejected' ? (
                  <button
                    onClick={() => handleStatusChange(selectedLetter.id, 'approved')}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Onayla
                  </button>
                ) : (
                  <button
                    onClick={() => handleStatusChange(selectedLetter.id, 'rejected')}
                    className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <XCircle className="w-4 h-4" />
                    Reddet
                  </button>
                )}
                <button
                  onClick={() => handleDelete(selectedLetter.id)}
                  disabled={deleting === selectedLetter.id}
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <Eye className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Detay görmek için bir mektup seçin</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
